import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { OrganizationData } from "../../../../models/OrganizationData";
import Checkbox from "../../../FormElement/Checkbox";
import DropdownSelect from "../../../FormElement/DropdownSelect";
import { LocationSelectQueryParams } from "../hook";

import "./styles.scss";

interface LocationSelectModalProps {
  allLocations: { id: number; name: string }[];
  selectedLocations: { id: number; name: string }[];
  setSelectedLocations: (locations: { id: number; name: string }[]) => void;
  onClose: () => void;
  changeQueryParams: (params: Partial<LocationSelectQueryParams>) => void;
  queryParams: LocationSelectQueryParams;
  organizations: OrganizationData[];
  isLoadingLocations: boolean;
  isLoadingOrganizations: boolean;
}

export const LocationSelectModal: React.FC<LocationSelectModalProps> = ({
  allLocations,
  selectedLocations,
  setSelectedLocations,
  onClose,
  changeQueryParams,
  queryParams,
  organizations,
  isLoadingLocations,
  isLoadingOrganizations,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`hookForm.locationSelect.modal.${key}`);

  const organizationOptions = [
    t("all"),
    ...organizations.map((organization) => organization.name),
  ];

  const selectedOrganizationName = queryParams.organizationId
    ? organizations.find(
        (organization) => organization.id === queryParams.organizationId
      )?.name || t("all")
    : t("all");

  const [temporarySelectedLocations, setTemporarySelectedLocations] =
    useState<{ id: number; name: string }[]>(selectedLocations);

  const handleCheck = (id: number) => {
    setTemporarySelectedLocations((prev) => {
      if (prev.some((l) => l.id === id)) {
        return prev.filter((l) => l.id !== id);
      }
      return [
        ...prev,
        { id, name: allLocations.find((l) => l.id === id)?.name || "" },
      ];
    });
  };

  const handleSubmit = () => {
    setSelectedLocations(temporarySelectedLocations);
    onClose();
  };

  // TODO: Create a better DropdownSelect component that uses value-label pairs instead of treating them as the same thing.
  // This will probably break if the organization name is not unique, or an organization has the name of t("all").
  const handleChangeOrganization = (organizationName: string) => {
    // Do nothing if the selection has not changed
    if (organizationName === selectedOrganizationName) {
      return;
    }

    if (organizationName === t("all")) {
      changeQueryParams({ organizationId: undefined });
      setTemporarySelectedLocations([]);
      return;
    }

    const organizationId = organizations.find(
      (organization) => organization.name === organizationName
    )?.id;
    if (organizationId) {
      changeQueryParams({ organizationId: organizationId.toString() });
    } else {
      changeQueryParams({ organizationId: undefined });
    }

    // Clear the selected locations when the organization changes,
    // to avoid unshown locations being registered as selected
    setTemporarySelectedLocations([]);
  };

  const handleClearSelectedLocations = () => {
    setTemporarySelectedLocations([]);
  };

  const handleSelectAllLocations = () => {
    setTemporarySelectedLocations(allLocations);
  };

  return (
    <div className="modal modal-default admin-location-select-modal">
      <div className="modal-mains">
        <button type="button" className="btn-close" onClick={onClose}></button>
        <div className="top-title">{t("title")}</div>
        <div className="admin-location-select-modal-organization-select">
          {isLoadingOrganizations ? (
            <div>{t("loading")}</div>
          ) : (
            <DropdownSelect
              fieldLabel={t("filter_by_organization")}
              value={selectedOrganizationName}
              items={organizationOptions}
              onChange={handleChangeOrganization}
            />
          )}
        </div>
        <div className="admin-location-select-modal-selection-controls">
          <button
            type="button"
            className="btn btn-border btn-border-red"
            onClick={handleClearSelectedLocations}
          >
            {t("clear_selected_locations")}
          </button>
          <button
            type="button"
            className="btn btn-border"
            onClick={handleSelectAllLocations}
          >
            {t("select_all_locations")}
          </button>
        </div>
        <div className="admin-location-select-modal-list">
          {isLoadingLocations ? (
            <div>{t("loading")}</div>
          ) : (
            allLocations.map((location) => (
              <div key={location.id}>
                <Checkbox
                  labelClassName="admin-location-select-modal-item"
                  id={location.id.toString()}
                  checked={temporarySelectedLocations.some(
                    (l) => l.id === location.id
                  )}
                  onChange={() => handleCheck(location.id)}
                  label={location.name}
                />
              </div>
            ))
          )}
        </div>
        <div className="admin-location-select-modal-buttons">
          <button type="button" className="btn btn-border" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="button" className="btn btn-blue" onClick={handleSubmit}>
            {t("submit")}
          </button>
        </div>
      </div>
    </div>
  );
};
